import React, { useMemo, useLayoutEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { aggregateUsersReportCharts } from './usersReportChartsUtils';

const CHART_H = 280;
const BAR_FILL = '#0ea5e9';
const BAR_FILL2 = '#22c55e';
const PIE_COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f97316', '#eab308', '#14b8a6', '#64748b'];

const ReportUsersCharts = ({ rows, loading, partial, formatLevelLabel, t }) => {
  useLayoutEffect(() => {
    const id = window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => window.cancelAnimationFrame(id);
  }, [rows?.length]);

  const { byRole, byActive, byLevel } = useMemo(
    () => aggregateUsersReportCharts(rows || [], formatLevelLabel),
    [rows, formatLevelLabel]
  );

  const pieData = useMemo(
    () =>
      (byRole || []).map((r) => ({
        name: r.name,
        value: r.count
      })),
    [byRole]
  );

  const activeChartData = useMemo(
    () =>
      (byActive || []).map((r) => ({
        label: r.key === 'yes' ? t('reports.users.activeYes') : t('reports.users.activeNo'),
        count: r.count
      })),
    [byActive, t]
  );

  if (loading) {
    return (
      <div className="mb-6 rounded-xl border border-slate-200/90 bg-slate-50/80 px-4 py-10 text-center text-sm font-semibold text-slate-600">
        {t('reports.dailyExercisesApi.chartsLoading')}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="mb-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-8 text-center text-sm text-slate-500">
        {t('reports.dailyExercisesApi.chartsEmpty')}
      </div>
    );
  }

  const chartBox = 'rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm shadow-slate-900/[0.04]';
  const title = 'text-sm font-bold text-slate-800 mb-2';

  return (
    <div className="mb-6 space-y-4">
      <div>
        <h2 className="text-base font-bold text-slate-900">{t('reports.usersCharts.sectionTitle')}</h2>
        <p className="mt-1 text-xs font-medium text-slate-500">{t('reports.dailyExercisesApi.chartsFootnote')}</p>
        {partial ? (
          <p className="mt-1 text-xs font-semibold text-amber-800/90">{t('reports.dailyExercisesApi.chartsPartialNote')}</p>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className={chartBox}>
          <h3 className={title}>{t('reports.usersCharts.chartRolesTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={88}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  isAnimationActive={false}
                >
                  {pieData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [v, t('reports.dailyExercisesApi.chartStudentCount')]} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={title}>{t('reports.usersCharts.chartActiveTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart data={activeChartData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v) => [v, t('reports.dailyExercisesApi.chartStudentCount')]} />
                <Bar dataKey="count" name={t('reports.dailyExercisesApi.chartStudentCount')} fill={BAR_FILL2} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={title}>{t('reports.usersCharts.chartLevelsTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart layout="vertical" data={byLevel} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 9 }} interval={0} />
                <Tooltip formatter={(v) => [v, t('reports.dailyExercisesApi.chartStudentCount')]} />
                <Bar dataKey="count" fill={BAR_FILL} radius={[0, 4, 4, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportUsersCharts;
